import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BeakerIcon,
  HeartIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CakeIcon,
  HomeIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  SparklesIcon,
  ChartPieIcon,
  TrophyIcon,
  FireIcon,
  AcademicCapIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { speciesApi, type Species } from '../api/species';
import { getBirds, type Bird } from '../api/birds';
import { asArray } from '../utils/asArray';
import { breedingApi, type BreedingPair, type Clutch } from '../api/breeding';
import { IUCNStatus, CITESStatus, Gender, BirdStatus, EggStatus } from '../utils/enums';
import { getAvatarUrl } from '../api/photos';
import PageHero from '../components/ui/PageHero';
import CountUpNumber from '../components/ui/CountUpNumber';
import {
  birdsListPath,
  breedingListPath,
  clutchesListPath,
  speciesListPath,
} from '../utils/listFilterPaths';

interface SpeciesStats {
  totalSpecies: number;
  totalWithPhotos: number;
  conservationStatusDistribution: Record<IUCNStatus, number>;
  citesStatusDistribution: Record<CITESStatus, number>;
  typeCategoryDistribution: Record<string, number>;
  originLocationDistribution: Record<string, number>;
  recentlyAdded: Species[];
  needsAttention: Species[];
}

interface BirdStats {
  totalBirds: number;
  totalWithPhotos: number;
  genderDistribution: Record<Gender, number>;
  statusDistribution: Record<BirdStatus, number>;
  speciesDistribution: Record<string, number>;
  cageDistribution: Record<string, number>;
  breedingPairs: number;
  juveniles: number;
  recentlyHatched: Bird[];
  needsAttention: Bird[];
}

interface BreedingStats {
  totalBreedingPairs: number;
  activeBreedingPairs: number;
  retiredBreedingPairs: number;
  totalClutches: number;
  totalEggs: number;
  totalHatched: number;
  totalFailed: number;
  hatchingRate: number;
  clutchesThisYear: number;
  eggsThisYear: number;
  hatchedThisYear: number;
  pairsBySpecies: Record<string, number>;
  clutchesByMonth: Record<string, number>;
  eggStatusDistribution: Record<EggStatus, number>;
  avgClutchSize: number;
  avgEggsPerClutch: number;
  recentClutches: Clutch[];
  productivePairs: { pair: BreedingPair; totalHatched: number }[];
  needsAttention: { pair: BreedingPair; reason: string }[];
  eggLifecycleMetrics: {
    averageIncubationDays: number;
    averageDaysToHatch: number;
    fertilityRate: number;
    hatchabilityRate: number;
  };
  eggFailureAnalysis: Record<EggStatus, { count: number; percentage: number }>;
  eggsByMonth: Record<string, number>;
  eggProductivityBySpecies: Record<string, { total: number; hatched: number; rate: number }>;
  clutchSizeDistribution: Record<number, number>;
  clutchSuccessRates: Record<string, number>;
  avgClutchFrequency: number;
  clutchCompletionRate: number;
  seasonalPatterns: {
    peakBreedingMonths: string[];
    lowActivityMonths: string[];
    avgEggsPerSeason: Record<string, number>;
  };
  performanceMetrics: {
    topPerformingClutchSizes: { size: number; successRate: number }[];
    averageClutchInterval: number;
    mostProductiveTimeOfYear: string;
  };
}

type TabType = 'species' | 'birds' | 'breeding';

export default function Dashboard() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('species');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days' | '12months' | '24months' | 'all'>('all');
  const [speciesStats, setSpeciesStats] = useState<SpeciesStats>({
    totalSpecies: 0,
    totalWithPhotos: 0,
    conservationStatusDistribution: {} as Record<IUCNStatus, number>,
    citesStatusDistribution: {} as Record<CITESStatus, number>,
    typeCategoryDistribution: {},
    originLocationDistribution: {},
    recentlyAdded: [],
    needsAttention: [],
  });
  const [birdStats, setBirdStats] = useState<BirdStats>({
    totalBirds: 0,
    totalWithPhotos: 0,
    genderDistribution: {} as Record<Gender, number>,
    statusDistribution: {} as Record<BirdStatus, number>,
    speciesDistribution: {},
    cageDistribution: {},
    breedingPairs: 0,
    juveniles: 0,
    recentlyHatched: [],
    needsAttention: [],
  });
  const [breedingStats, setBreedingStats] = useState<BreedingStats>({
    totalBreedingPairs: 0,
    activeBreedingPairs: 0,
    retiredBreedingPairs: 0,
    totalClutches: 0,
    totalEggs: 0,
    totalHatched: 0,
    totalFailed: 0,
    hatchingRate: 0,
    clutchesThisYear: 0,
    eggsThisYear: 0,
    hatchedThisYear: 0,
    pairsBySpecies: {},
    clutchesByMonth: {},
    eggStatusDistribution: {} as Record<EggStatus, number>,
    avgClutchSize: 0,
    avgEggsPerClutch: 0,
    recentClutches: [],
    productivePairs: [],
    needsAttention: [],
    eggLifecycleMetrics: {
      averageIncubationDays: 0,
      averageDaysToHatch: 0,
      fertilityRate: 0,
      hatchabilityRate: 0,
    },
    eggFailureAnalysis: {} as Record<EggStatus, { count: number; percentage: number }>,
    eggsByMonth: {},
    eggProductivityBySpecies: {},
    clutchSizeDistribution: {},
    clutchSuccessRates: {},
    avgClutchFrequency: 0,
    clutchCompletionRate: 0,
    seasonalPatterns: {
      peakBreedingMonths: [],
      lowActivityMonths: [],
      avgEggsPerSeason: {},
    },
    performanceMetrics: {
      topPerformingClutchSizes: [],
      averageClutchInterval: 0,
      mostProductiveTimeOfYear: '',
    },
  });
  const [allBreedingPairs, setAllBreedingPairs] = useState<BreedingPair[]>([]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) {
      setLoading(false);
      return;
    }
    loadDashboardData();
  }, [dateRange, isAuthenticated, authLoading]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on selected timeframe
      const now = new Date();
      let startDate: Date;
      
      switch (dateRange) {
        case '7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '12months':
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        case '24months':
          startDate = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
          break;
        case 'all':
          startDate = new Date(0); // Beginning of time
          break;
        default:
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }
      
      // Load species data
      const speciesResponse = await speciesApi.getAll();
      const species = asArray<Species>(speciesResponse.data);

      // Load birds data
      const birds = asArray<Bird>(await getBirds());

      // Load breeding pairs data
      const breedingResponse = await breedingApi.getAll();
      const breedingPairs = asArray<BreedingPair>(breedingResponse.data);

      // Species created within the selected range. When 'all' is selected the
      // startDate is the epoch, so every species is included.
      const filteredSpecies = species.filter(
        s => new Date(s.createdAt || s.updatedAt || 0) >= startDate,
      );

      // Calculate species statistics
      const speciesStatsData: SpeciesStats = {
        totalSpecies: filteredSpecies.length,
        totalWithPhotos: filteredSpecies.filter(s => s.photos && s.photos.length > 0).length,
        conservationStatusDistribution: {} as Record<IUCNStatus, number>,
        citesStatusDistribution: {} as Record<CITESStatus, number>,
        typeCategoryDistribution: {},
        originLocationDistribution: {},
        recentlyAdded: [...filteredSpecies]
          .sort(
            (a, b) =>
              new Date(b.createdAt || b.updatedAt || 0).getTime() -
              new Date(a.createdAt || a.updatedAt || 0).getTime(),
          )
          .slice(0, 5),
        needsAttention: filteredSpecies.filter(s => 
          s.iucnStatus === IUCNStatus.CRITICALLY_ENDANGERED || 
          s.iucnStatus === IUCNStatus.ENDANGERED ||
          !s.avatarPhotoId
        ).slice(0, 5),
      };

      // Calculate species distributions
      filteredSpecies.forEach(s => {
        speciesStatsData.conservationStatusDistribution[s.iucnStatus] = 
          (speciesStatsData.conservationStatusDistribution[s.iucnStatus] || 0) + 1;
        speciesStatsData.citesStatusDistribution[s.citesStatus] = 
          (speciesStatsData.citesStatusDistribution[s.citesStatus] || 0) + 1;
        speciesStatsData.typeCategoryDistribution[s.typeCategory] = 
          (speciesStatsData.typeCategoryDistribution[s.typeCategory] || 0) + 1;
        speciesStatsData.originLocationDistribution[s.originLocation] = 
          (speciesStatsData.originLocationDistribution[s.originLocation] || 0) + 1;
      });

      // Calculate bird statistics
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      
      // Filter birds based on timeframe
      const filteredBirds = birds.filter(bird => {
        const birdDate = new Date(bird.createdAt || bird.updatedAt || bird.hatchingDate || 0);
        return birdDate >= startDate;
      });
      
      const birdStatsData: BirdStats = {
        totalBirds: filteredBirds.length,
        totalWithPhotos: filteredBirds.filter(b => b.photos && b.photos.length > 0).length,
        genderDistribution: {} as Record<Gender, number>,
        statusDistribution: {} as Record<BirdStatus, number>,
        speciesDistribution: {},
        cageDistribution: {},
        breedingPairs: 0,
        juveniles: filteredBirds.filter(b => new Date(b.hatchingDate) > threeMonthsAgo).length,
        recentlyHatched: filteredBirds
          .filter(b => new Date(b.hatchingDate) > oneMonthAgo)
          .sort((a, b) => new Date(b.hatchingDate).getTime() - new Date(a.hatchingDate).getTime())
          .slice(0, 5),
        needsAttention: filteredBirds.filter(b => 
          !b.avatarPhotoId || 
          b.status === BirdStatus.SICK ||
          b.status === BirdStatus.DECEASED
        ).slice(0, 5),
      };

      // Calculate bird distributions
      filteredBirds.forEach(b => {
        birdStatsData.genderDistribution[b.gender] = 
          (birdStatsData.genderDistribution[b.gender] || 0) + 1;
        birdStatsData.statusDistribution[b.status] = 
          (birdStatsData.statusDistribution[b.status] || 0) + 1;
        
        if (b.species) {
          const speciesName = b.species.commonName;
          birdStatsData.speciesDistribution[speciesName] = 
            (birdStatsData.speciesDistribution[speciesName] || 0) + 1;
        }
        
        birdStatsData.cageDistribution[b.cageNumber] = 
          (birdStatsData.cageDistribution[b.cageNumber] || 0) + 1;
      });

      // Calculate breeding pairs (simplified - birds in same cage with different genders and breeding status)
      const cageGroups = filteredBirds.reduce((acc, bird) => {
        if (!acc[bird.cageNumber]) acc[bird.cageNumber] = [];
        acc[bird.cageNumber].push(bird);
        return acc;
      }, {} as Record<string, Bird[]>);
      
      birdStatsData.breedingPairs = Object.values(cageGroups).filter(cage => {
        const males = cage.filter(b => b.gender === Gender.MALE && b.status === BirdStatus.BREEDING);
        const females = cage.filter(b => b.gender === Gender.FEMALE && b.status === BirdStatus.BREEDING);
        return males.length > 0 && females.length > 0;
      }).length;

      // Calculate breeding pair statistics
      const currentYear = new Date().getFullYear();
      const allClutches = breedingPairs.flatMap(pair => pair.clutches || []);
      const allEggs = allClutches.flatMap(clutch => clutch.eggs || []);

      // A pair counts for the selected range if it was formed within the range
      // or has any breeding activity (a clutch) inside the range. With 'all'
      // selected (startDate = epoch) every pair is included.
      const filteredPairs = breedingPairs.filter(pair => {
        const formed = new Date(pair.pairingBeginDate || pair.createdAt || 0);
        if (formed >= startDate) return true;
        return (pair.clutches || []).some(c => new Date(c.date) >= startDate);
      });

      // Filter breeding data based on timeframe
      const filteredClutches = allClutches.filter(clutch => {
        const clutchDate = new Date(clutch.date);
        return clutchDate >= startDate;
      });
      
      const filteredEggs = filteredClutches.flatMap(clutch => clutch.eggs || []);
      
      const hatchedEggs = filteredEggs.filter(egg => egg.status === EggStatus.HATCHED);
      const failedEggs = filteredEggs.filter(egg => 
        egg.status === EggStatus.NOT_FERTILIZED ||
        egg.status === EggStatus.BROKEN ||
        egg.status === EggStatus.ABANDONED ||
        egg.status === EggStatus.DEAD_IN_SHELL ||
        egg.status === EggStatus.DEAD_BEFORE_BANDING
      );
      const clutchesThisYear = allClutches.filter(clutch => 
        new Date(clutch.date).getFullYear() === currentYear
      );
      const eggsThisYear = clutchesThisYear.flatMap(clutch => clutch.eggs || []);
      const hatchedThisYear = eggsThisYear.filter(egg => egg.status === EggStatus.HATCHED);

      // Build a multi-year, range-aware monthly series. Keys are like "Jan 2025"
      // so data spanning multiple calendar years is preserved (the old version
      // only ever counted the current year and silently dropped the rest).
      const monthKeyOf = (d: Date) =>
        d.toLocaleString('default', { month: 'short', year: 'numeric' });
      const MONTH_SHORT = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ];
      const buildMonthlySeries = (dates: Date[]): string[] => {
        const valid = dates.filter(d => !isNaN(d.getTime()));
        let seriesStart =
          dateRange === 'all'
            ? valid.length
              ? new Date(Math.min(...valid.map(d => d.getTime())))
              : new Date(now)
            : new Date(startDate);
        seriesStart = new Date(seriesStart.getFullYear(), seriesStart.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 1);
        const keys: string[] = [];
        const cursor = new Date(seriesStart);
        while (cursor <= end) {
          keys.push(monthKeyOf(cursor));
          cursor.setMonth(cursor.getMonth() + 1);
        }
        // Cap to the most recent 24 months to keep the charts readable.
        return keys.slice(-24);
      };
      const tallyByMonth = (dates: Date[]): Record<string, number> => {
        const keys = buildMonthlySeries(dates);
        const map: Record<string, number> = {};
        keys.forEach(k => (map[k] = 0));
        dates.forEach(d => {
          if (isNaN(d.getTime())) return;
          const key = monthKeyOf(d);
          if (key in map) map[key]++;
        });
        return map;
      };

      // Month-of-year aggregation (Jan..Dec, across all years in range) used for
      // seasonal pattern detection where the calendar year is irrelevant.
      const clutchesByMonthOfYear: Record<string, number> = {};
      MONTH_SHORT.forEach(m => (clutchesByMonthOfYear[m] = 0));
      filteredClutches.forEach(clutch => {
        const clutchDate = new Date(clutch.date);
        if (!isNaN(clutchDate.getTime())) {
          clutchesByMonthOfYear[MONTH_SHORT[clutchDate.getMonth()]]++;
        }
      });

      const clutchesByMonth = tallyByMonth(
        filteredClutches.map(c => new Date(c.date)),
      );

      // Group pairs by species
      const pairsBySpecies: Record<string, number> = {};
      filteredPairs.forEach(pair => {
        // Try to get species from male bird first, then female bird, then show "Unspecified Species"
        const maleSpecies = pair.maleBirdEntity?.species?.commonName;
        const femaleSpecies = pair.femaleBirdEntity?.species?.commonName;
        const species = maleSpecies || femaleSpecies || 'Unspecified Species';
        pairsBySpecies[species] = (pairsBySpecies[species] || 0) + 1;
      });

      // Calculate egg status distribution
      const eggStatusDistribution: Record<EggStatus, number> = {} as Record<EggStatus, number>;
      filteredEggs.forEach(egg => {
        eggStatusDistribution[egg.status] = (eggStatusDistribution[egg.status] || 0) + 1;
      });

      // Find productive pairs (top 5 by total hatched)
      const productivePairs = breedingPairs
        .map(pair => ({
          pair,
          totalHatched: (pair.clutches || [])
            .filter(clutch => new Date(clutch.date) >= startDate)
            .flatMap(clutch => clutch.eggs || [])
            .filter(egg => egg.status === EggStatus.HATCHED).length
        }))
        .filter(p => p.totalHatched > 0)
        .sort((a, b) => b.totalHatched - a.totalHatched)
        .slice(0, 5);

      // Find pairs needing attention
      const pairsNeedingAttention = breedingPairs
        .map(pair => {
          const reasons = [];
          const isActive = !pair.pairingEndDate;
          const hasRecentClutch = (pair.clutches || []).some(clutch => 
            new Date(clutch.date) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) // 3 months ago
          );
          
          if (isActive && !hasRecentClutch) {
            reasons.push('No recent clutches');
          }
          
          const failedEggsCount = (pair.clutches || [])
            .filter(clutch => new Date(clutch.date) >= startDate)
            .flatMap(clutch => clutch.eggs || [])
            .filter(egg => 
              egg.status === EggStatus.NOT_FERTILIZED ||
              egg.status === EggStatus.BROKEN ||
              egg.status === EggStatus.ABANDONED ||
              egg.status === EggStatus.DEAD_IN_SHELL ||
              egg.status === EggStatus.DEAD_BEFORE_BANDING
            ).length;
          const totalEggsCount = (pair.clutches || [])
            .filter(clutch => new Date(clutch.date) >= startDate)
            .flatMap(clutch => clutch.eggs || []).length;
            
          if (totalEggsCount > 0 && failedEggsCount / totalEggsCount > 0.5) {
            reasons.push('High failure rate');
          }

          return reasons.length > 0 ? { pair, reason: reasons.join(', ') } : null;
        })
        .filter(Boolean)
        .slice(0, 5) as { pair: BreedingPair; reason: string }[];

      const breedingStatsData: BreedingStats = {
        totalBreedingPairs: filteredPairs.length,
        activeBreedingPairs: filteredPairs.filter(pair => !pair.pairingEndDate).length,
        retiredBreedingPairs: filteredPairs.filter(pair => !!pair.pairingEndDate).length,
        totalClutches: filteredClutches.length,
        totalEggs: filteredEggs.length,
        totalHatched: hatchedEggs.length,
        totalFailed: failedEggs.length,
        hatchingRate: filteredEggs.length > 0 ? Math.round((hatchedEggs.length / filteredEggs.length) * 100) : 0,
        clutchesThisYear: clutchesThisYear.length,
        eggsThisYear: eggsThisYear.length,
        hatchedThisYear: hatchedThisYear.length,
        pairsBySpecies,
        clutchesByMonth,
        eggStatusDistribution,
        avgClutchSize: filteredClutches.length > 0 ? Math.round(filteredEggs.length / filteredClutches.length * 10) / 10 : 0,
        avgEggsPerClutch: filteredClutches.length > 0 ? Math.round(filteredEggs.length / filteredClutches.length * 10) / 10 : 0,
        recentClutches: filteredClutches
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 5),
        productivePairs,
        needsAttention: pairsNeedingAttention,

        // Calculate comprehensive egg analytics
        eggLifecycleMetrics: (() => {
          const fertilizedEggs = filteredEggs.filter(egg => 
            egg.status !== EggStatus.NOT_FERTILIZED && egg.status !== EggStatus.BROKEN
          );
          const incubatingEggs = filteredEggs.filter(egg => 
            egg.incubationStartDate && egg.status === EggStatus.INCUBATING
          );
          const hatchedWithDates = filteredEggs.filter(egg => 
            egg.status === EggStatus.HATCHED && egg.hatchingDate && egg.incubationStartDate
          );

          let avgIncubationDays = 0;
          let avgDaysToHatch = 0;

          if (incubatingEggs.length > 0) {
            const incubationDays = incubatingEggs
              .filter(egg => egg.incubationDays !== null)
              .map(egg => egg.incubationDays!);
            avgIncubationDays = incubationDays.length > 0 
              ? Math.round(incubationDays.reduce((sum, days) => sum + days, 0) / incubationDays.length)
              : 0;
          }

          if (hatchedWithDates.length > 0) {
            const hatchingDurations = hatchedWithDates.map(egg => {
              const incubationStart = new Date(egg.incubationStartDate!);
              const hatchDate = new Date(egg.hatchingDate!);
              return Math.round((hatchDate.getTime() - incubationStart.getTime()) / (1000 * 60 * 60 * 24));
            });
            avgDaysToHatch = Math.round(hatchingDurations.reduce((sum, days) => sum + days, 0) / hatchingDurations.length);
          }

          return {
            averageIncubationDays: avgIncubationDays,
            averageDaysToHatch: avgDaysToHatch,
            fertilityRate: filteredEggs.length > 0 ? Math.round((fertilizedEggs.length / filteredEggs.length) * 100) : 0,
            hatchabilityRate: fertilizedEggs.length > 0 ? Math.round((hatchedEggs.length / fertilizedEggs.length) * 100) : 0,
          };
        })(),

        // Calculate egg failure analysis
        eggFailureAnalysis: (() => {
          const failureTypes = [
            EggStatus.NOT_FERTILIZED,
            EggStatus.BROKEN,
            EggStatus.ABANDONED,
            EggStatus.DEAD_IN_SHELL,
            EggStatus.DEAD_BEFORE_BANDING
          ];
          
          const analysis: Record<EggStatus, { count: number; percentage: number }> = {} as any;
          
          failureTypes.forEach(status => {
            const count = filteredEggs.filter(egg => egg.status === status).length;
            analysis[status] = {
              count,
              percentage: filteredEggs.length > 0 ? Math.round((count / filteredEggs.length) * 100) : 0
            };
          });
          
          return analysis;
        })(),

        // Eggs laid per month, multi-year and range-aware.
        eggsByMonth: tallyByMonth(filteredEggs.map(e => new Date(e.layDate))),

        // Calculate egg productivity by species
        eggProductivityBySpecies: (() => {
          const speciesEggData: Record<string, { total: number; hatched: number; rate: number }> = {};
          
          breedingPairs.forEach(pair => {
            // Try to get species from male bird first, then female bird, then show "Unspecified Species"
            const maleSpecies = pair.maleBirdEntity?.species?.commonName;
            const femaleSpecies = pair.femaleBirdEntity?.species?.commonName;
            const species = maleSpecies || femaleSpecies || 'Unspecified Species';
            const pairEggs = (pair.clutches || [])
              .filter(clutch => new Date(clutch.date) >= startDate)
              .flatMap(clutch => clutch.eggs || []);
            const pairHatched = pairEggs.filter(egg => egg.status === EggStatus.HATCHED);
            
            if (!speciesEggData[species]) {
              speciesEggData[species] = { total: 0, hatched: 0, rate: 0 };
            }
            
            speciesEggData[species].total += pairEggs.length;
            speciesEggData[species].hatched += pairHatched.length;
          });
          
          Object.keys(speciesEggData).forEach(species => {
            const data = speciesEggData[species];
            data.rate = data.total > 0 ? Math.round((data.hatched / data.total) * 100) : 0;
          });
          
          return speciesEggData;
        })(),

        // Calculate clutch size distribution
        clutchSizeDistribution: (() => {
          const distribution: Record<number, number> = {};
          
          filteredClutches.forEach(clutch => {
            const size = clutch.eggs?.length || 0;
            distribution[size] = (distribution[size] || 0) + 1;
          });
          
          return distribution;
        })(),

        // Clutch success rate per month (multi-year, range-aware). A clutch is
        // "successful" when at least half of its eggs hatched.
        clutchSuccessRates: (() => {
          const successRates: Record<string, number> = {};
          const keys = buildMonthlySeries(
            filteredClutches.map(c => new Date(c.date)),
          );
          keys.forEach(key => {
            const monthClutches = filteredClutches.filter(
              clutch => monthKeyOf(new Date(clutch.date)) === key,
            );
            if (monthClutches.length > 0) {
              const successfulClutches = monthClutches.filter(clutch => {
                const eggs = clutch.eggs || [];
                const hatchedCount = eggs.filter(egg => egg.status === EggStatus.HATCHED).length;
                return eggs.length > 0 && (hatchedCount / eggs.length) >= 0.5;
              });
              successRates[key] = Math.round((successfulClutches.length / monthClutches.length) * 100);
            } else {
              successRates[key] = 0;
            }
          });
          return successRates;
        })(),

        // Average clutches per pair across the selected range.
        avgClutchFrequency: (() => {
          if (filteredPairs.length === 0) return 0;
          return Math.round((filteredClutches.length / filteredPairs.length) * 10) / 10;
        })(),

        // Calculate clutch completion rate
        clutchCompletionRate: (() => {
          if (filteredClutches.length === 0) return 0;
          const completedClutches = filteredClutches.filter(clutch => {
            const eggs = clutch.eggs || [];
            if (eggs.length === 0) return false;
            // A clutch is considered "completed" if all eggs have reached a final status
            const finalStatuses = [EggStatus.HATCHED, EggStatus.FLEDGED, EggStatus.BANDED, ...Object.values(EggStatus).filter(status => 
              [EggStatus.NOT_FERTILIZED, EggStatus.BROKEN, EggStatus.ABANDONED, EggStatus.DEAD_IN_SHELL, EggStatus.DEAD_BEFORE_BANDING].includes(status)
            )];
            return eggs.every(egg => finalStatuses.includes(egg.status));
          });
          
          return Math.round((completedClutches.length / filteredClutches.length) * 100);
        })(),

        // Calculate seasonal patterns from month-of-year activity (year-agnostic).
        seasonalPatterns: (() => {
          // Only rank months that actually had clutches, so we don't report
          // empty months as "peak" or "low" activity.
          const activeMonths = Object.entries(clutchesByMonthOfYear).filter(
            ([, count]) => count > 0,
          );
          const sortedByActivity = [...activeMonths].sort(([, a], [, b]) => b - a);

          const seasons: Record<string, string[]> = {
            Spring: ['Mar', 'Apr', 'May'],
            Summer: ['Jun', 'Jul', 'Aug'],
            Fall: ['Sep', 'Oct', 'Nov'],
            Winter: ['Dec', 'Jan', 'Feb'],
          };

          // Real per-season egg counts derived from actual lay dates.
          const eggsByMonthOfYear: Record<string, number> = {};
          MONTH_SHORT.forEach(m => (eggsByMonthOfYear[m] = 0));
          filteredEggs.forEach(egg => {
            const layDate = new Date(egg.layDate);
            if (!isNaN(layDate.getTime())) {
              eggsByMonthOfYear[MONTH_SHORT[layDate.getMonth()]]++;
            }
          });

          const avgEggsPerSeason: Record<string, number> = {};
          Object.entries(seasons).forEach(([season, months]) => {
            avgEggsPerSeason[season] = months.reduce(
              (sum, m) => sum + (eggsByMonthOfYear[m] || 0),
              0,
            );
          });

          return {
            peakBreedingMonths: sortedByActivity.slice(0, 3).map(([month]) => month),
            lowActivityMonths: sortedByActivity.slice(-3).map(([month]) => month),
            avgEggsPerSeason,
          };
        })(),

        // Calculate performance metrics
        performanceMetrics: (() => {
          // Top performing clutch sizes - calculate clutch size distribution first
          const clutchSizeDistribution: Record<number, number> = {};
          filteredClutches.forEach(clutch => {
            const size = clutch.eggs?.length || 0;
            clutchSizeDistribution[size] = (clutchSizeDistribution[size] || 0) + 1;
          });

          const clutchSizePerformance = Object.entries(clutchSizeDistribution)
            .map(([sizeStr, _count]) => {
              const size = parseInt(sizeStr);
              const clutchesOfSize = filteredClutches.filter(clutch => (clutch.eggs?.length || 0) === size);
              const successfulClutches = clutchesOfSize.filter(clutch => {
                const eggs = clutch.eggs || [];
                const hatchedCount = eggs.filter(egg => egg.status === EggStatus.HATCHED).length;
                return eggs.length > 0 && (hatchedCount / eggs.length) >= 0.5;
              });
              
              return {
                size,
                successRate: clutchesOfSize.length > 0 ? Math.round((successfulClutches.length / clutchesOfSize.length) * 100) : 0
              };
            })
            .sort((a, b) => b.successRate - a.successRate)
            .slice(0, 5);

          // Average clutch interval
          let totalIntervals = 0;
          let intervalCount = 0;
          
          breedingPairs.forEach(pair => {
            const pairClutches = (pair.clutches || [])
              .filter(clutch => new Date(clutch.date) >= startDate)
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            for (let i = 1; i < pairClutches.length; i++) {
              const interval = Math.round((new Date(pairClutches[i].date).getTime() - new Date(pairClutches[i-1].date).getTime()) / (1000 * 60 * 60 * 24));
              totalIntervals += interval;
              intervalCount++;
            }
          });
          
          const avgInterval = intervalCount > 0 ? Math.round(totalIntervals / intervalCount) : 0;
          
          // Most productive month of the year (year-agnostic).
          const monthlyActivity = Object.entries(clutchesByMonthOfYear);
          const mostProductiveMonth = monthlyActivity.reduce(
            (max, current) => (current[1] > max[1] ? current : max),
            ['', 0] as [string, number],
          )[0];
          
          return {
            topPerformingClutchSizes: clutchSizePerformance,
            averageClutchInterval: avgInterval,
            mostProductiveTimeOfYear: mostProductiveMonth,
          };
        })(),
      };

      setSpeciesStats(speciesStatsData);
      setBirdStats(birdStatsData);
      setBreedingStats(breedingStatsData);
      setAllBreedingPairs(breedingPairs);
    } catch (error: any) {
      if (error?.status !== 401) {
        console.error('Error loading dashboard data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'species', name: 'Species Analytics', icon: BeakerIcon },
    { id: 'birds', name: 'Birds Analytics', icon: UserGroupIcon },
    { id: 'breeding', name: 'Breeding Analytics', icon: ClipboardDocumentListIcon },
  ];

  const getSpeciesMainStats = () => [
    { 
      name: 'Total Species', 
      value: speciesStats.totalSpecies.toString(), 
      icon: BeakerIcon,
      change: dateRange === 'all' ? 'All time' : 'In range',
      changeType: 'neutral' as const,
      href: speciesListPath()
    },
    { 
      name: 'Species with Photos', 
      value: speciesStats.totalWithPhotos.toString(), 
      icon: PhotoIcon,
      change: `${speciesStats.totalSpecies > 0 ? Math.round((speciesStats.totalWithPhotos / speciesStats.totalSpecies) * 100) : 0}%`,
      changeType: 'neutral' as const,
      href: speciesListPath('withPhotos')
    },
    { 
      name: 'Conservation Concern', 
      value: (
        (speciesStats.conservationStatusDistribution[IUCNStatus.CRITICALLY_ENDANGERED] || 0) +
        (speciesStats.conservationStatusDistribution[IUCNStatus.ENDANGERED] || 0) +
        (speciesStats.conservationStatusDistribution[IUCNStatus.VULNERABLE] || 0)
      ).toString(), 
      icon: ExclamationTriangleIcon,
      change: 'Monitor closely',
      changeType: 'decrease' as const,
      href: speciesListPath('conservation')
    },
  ];

  const getBirdsMainStats = () => [
    { 
      name: 'Total Birds', 
      value: birdStats.totalBirds.toString(), 
      icon: UserGroupIcon,
      change: dateRange === 'all' ? 'All time' : 'In range',
      changeType: 'neutral' as const,
      href: birdsListPath()
    },
    { 
      name: 'Breeding Pairs', 
      value: birdStats.breedingPairs.toString(), 
      icon: HeartIcon,
      change: 'Active breeding',
      changeType: 'increase' as const,
      href: birdsListPath({ status: BirdStatus.BREEDING })
    },
    { 
      name: 'Recent Hatchlings', 
      value: birdStats.juveniles.toString(), 
      icon: CakeIcon,
      change: 'Last 3 months',
      changeType: 'neutral' as const,
      href: birdsListPath({ filter: 'juveniles' })
    },
  ];

  const getBreedingMainStats = () => [
    { 
      name: 'Active Breeding Pairs', 
      value: breedingStats.activeBreedingPairs.toString(), 
      icon: HeartIcon,
      change: `${breedingStats.totalBreedingPairs} total`,
      changeType: 'neutral' as const,
      href: breedingListPath({ status: 'active' })
    },
    { 
      name: 'Clutches This Year', 
      value: breedingStats.clutchesThisYear.toString(), 
      icon: ClipboardDocumentListIcon,
      change: `${breedingStats.totalClutches} all-time`,
      changeType: 'increase' as const,
      href: clutchesListPath({ filter: 'thisYear' })
    },
    { 
      name: 'Hatching Rate', 
      value: `${breedingStats.hatchingRate}%`, 
      icon: CheckCircleIcon,
      change: `${breedingStats.totalHatched}/${breedingStats.totalEggs} eggs`,
      changeType: breedingStats.hatchingRate > 70 ? 'increase' as const : breedingStats.hatchingRate > 50 ? 'neutral' as const : 'decrease' as const,
      href: clutchesListPath({ status: 'hatched' })
    },
  ];

  const mainStats = activeTab === 'species' ? getSpeciesMainStats() : activeTab === 'birds' ? getBirdsMainStats() : getBreedingMainStats();

  const parseStatValue = (rawValue: string) => {
    const match = rawValue.match(/^-?\d+(\.\d+)?/);
    if (!match) {
      return { numeric: 0, suffix: '', decimals: 0, hasNumeric: false };
    }
    const numeric = Number.parseFloat(match[0]);
    const suffix = rawValue.slice(match[0].length);
    const decimals = match[0].includes('.') ? (match[0].split('.')[1]?.length ?? 0) : 0;
    return { numeric, suffix, decimals, hasNumeric: true };
  };

  const getStatusColor = (status: IUCNStatus) => {
    switch (status) {
      case IUCNStatus.CRITICALLY_ENDANGERED:
        return 'bg-red-500';
      case IUCNStatus.ENDANGERED:
        return 'bg-orange-500';
      case IUCNStatus.VULNERABLE:
        return 'bg-yellow-500';
      case IUCNStatus.NEAR_THREATENED:
        return 'bg-yellow-300';
      case IUCNStatus.LEAST_CONCERN:
        return 'bg-green-500';
      default:
        return 'bg-slate-300';
    }
  };

  const getCitesColor = (status: CITESStatus) => {
    switch (status) {
      case CITESStatus.APPENDIX_I:
        return 'bg-red-500';
      case CITESStatus.APPENDIX_II:
        return 'bg-yellow-500';
      case CITESStatus.APPENDIX_III:
        return 'bg-cyan-500';
      default:
        return 'bg-slate-300';
    }
  };

  const getGenderColor = (gender: Gender) => {
    switch (gender) {
      case Gender.MALE:
        return 'bg-cyan-500';
      case Gender.FEMALE:
        return 'bg-pink-500';
      case Gender.UNKNOWN:
        return 'bg-slate-300';
      default:
        return 'bg-slate-300';
    }
  };

  const getBirdStatusColor = (status: BirdStatus) => {
    switch (status) {
      case BirdStatus.BREEDING:
        return 'bg-green-500';
      case BirdStatus.NOT_BREEDING:
        return 'bg-cyan-500';
      case BirdStatus.SICK:
        return 'bg-red-500';
      case BirdStatus.DECEASED:
        return 'bg-slate-300';
      default:
        return 'bg-slate-300';
    }
  };

  const isFailedEggStatus = (status: EggStatus) => {
    return status === EggStatus.NOT_FERTILIZED ||
           status === EggStatus.BROKEN ||
           status === EggStatus.ABANDONED ||
           status === EggStatus.DEAD_IN_SHELL ||
           status === EggStatus.DEAD_BEFORE_BANDING;
  };

  const formatEggStatusLabel = (status: string) =>
    status.toLowerCase().replace(/_/g, ' ');

  const getEggStatusColor = (status: EggStatus) => {
    if (status === EggStatus.HATCHED) return 'bg-green-500';
    if (status === EggStatus.INCUBATING) return 'bg-yellow-500';
    if (status === EggStatus.LAID) return 'bg-cyan-500';
    if (isFailedEggStatus(status)) return 'bg-red-500';
    return 'bg-slate-300';
  };

  // Bar width as a percentage of the largest value in a map. Returns 0 (so the
  // bar simply doesn't render) instead of NaN when every value is zero/empty.
  const barWidthPct = (count: number, values: number[]): number => {
    const max = values.length ? Math.max(...values) : 0;
    if (!max || !isFinite(max)) return 0;
    return Math.max(Math.round((count / max) * 100), count > 0 ? 5 : 0);
  };

  const getEggStatusColorClass = (status: EggStatus) => {
    if (status === EggStatus.HATCHED) return 'bg-emerald-100 text-emerald-800';
    if (status === EggStatus.INCUBATING) return 'bg-amber-100 text-amber-800';
    if (status === EggStatus.LAID) return 'bg-cyan-100 text-cyan-800';
    if (isFailedEggStatus(status)) return 'bg-rose-100 text-rose-800';
    return 'bg-violet-100 text-violet-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Dashboard"
        subtitle="Overview of your bird species collection and breeding program"
        actions={
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            className="input min-w-48"
            aria-label="Filter dashboard by date range"
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
            <option value="12months">Last 12 months</option>
            <option value="24months">Last 24 months</option>
            <option value="all">All data</option>
          </select>
        }
      />
      
      {/* Tab Navigation */}
      <div className="mt-8">
        <div className="soft-separator border-b">
          <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`tab-pill whitespace-nowrap ${
                    activeTab === tab.id ? 'tab-pill-active' : ''
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      
      {/* Main Stats */}
      <div className="mt-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {mainStats.map((item, index) => (
            <Link
              key={item.name}
              to={item.href}
              className="interactive-card glass-panel fade-up relative overflow-hidden rounded-2xl px-4 pb-12 pt-5 sm:px-6 sm:pt-6"
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <dt>
                <div className="absolute rounded-md bg-gradient-to-br from-cyan-500 to-violet-500 p-3">
                  <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-slate-500">{item.name}</p>
              </dt>
              <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
                <p className="text-2xl font-semibold text-slate-900">
                  {(() => {
                    const parsed = parseStatValue(item.value);
                    if (!parsed.hasNumeric) return item.value;
                    return (
                      <CountUpNumber
                        value={parsed.numeric}
                        decimals={parsed.decimals}
                        suffix={parsed.suffix}
                      />
                    );
                  })()}
                </p>
                <p
                  className={`ml-2 flex items-baseline text-sm font-semibold ${
                    item.changeType === 'increase'
                      ? 'text-green-600'
                      : item.changeType === 'decrease'
                      ? 'text-red-600'
                      : 'text-slate-600'
                  }`}
                >
                  {item.change}
                </p>
              </dd>
            </Link>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'species' && (
        <div className="fade-up">
          {/* Species Analytics Charts */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Conservation Status Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ShieldCheckIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Conservation Status Distribution
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    {Object.entries(speciesStats.conservationStatusDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center">
                        <div className="flex items-center flex-1">
                          <div className={`h-3 w-3 rounded-full ${getStatusColor(status as IUCNStatus)} mr-3`} />
                          <span className="text-sm text-slate-600 flex-1">{status.replace(/_/g, ' ')}</span>
                          <span className="text-sm font-medium text-slate-900">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* CITES Status Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <GlobeAltIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        CITES Status Distribution
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    {Object.entries(speciesStats.citesStatusDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center">
                        <div className="flex items-center flex-1">
                          <div className={`h-3 w-3 rounded-full ${getCitesColor(status as CITESStatus)} mr-3`} />
                          <span className="text-sm text-slate-600 flex-1">{status.replace(/_/g, ' ')}</span>
                          <span className="text-sm font-medium text-slate-900">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Type Categories */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Species by Type Category
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(speciesStats.typeCategoryDistribution)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([category, count]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate">{category}</span>
                        <span className="text-sm font-medium text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Geographic Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <GlobeAltIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Species by Origin Location
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(speciesStats.originLocationDistribution)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([location, count]) => (
                      <div key={location} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate">{location}</span>
                        <span className="text-sm font-medium text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Species Recent Activity & Attention Needed */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recently Added Species */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Recently Added Species</h3>
                <div className="mt-5">
                  {speciesStats.recentlyAdded.length === 0 ? (
                    <p className="text-sm text-slate-500">No species added yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {speciesStats.recentlyAdded.map((species) => (
                        <div key={species.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {species.avatarPhotoId ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={getAvatarUrl(species.avatarPhotoId) || '/placeholder-species.jpg'}
                                alt={species.commonName}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <BeakerIcon className="h-4 w-4 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {species.commonName}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {species.fullTaxonomicName}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              species.iucnStatus === IUCNStatus.CRITICALLY_ENDANGERED ? 'bg-rose-100 text-rose-800' :
                              species.iucnStatus === IUCNStatus.ENDANGERED ? 'bg-orange-100 text-orange-800' :
                              species.iucnStatus === IUCNStatus.VULNERABLE ? 'bg-amber-100 text-amber-800' :
                              'bg-emerald-100 text-emerald-800'
                            }`}>
                              {species.iucnStatus}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={speciesListPath('recent')}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    View recently added
                  </Link>
                </div>
              </div>
            </div>

            {/* Species Needing Attention */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Species Needing Attention</h3>
                <div className="mt-5">
                  {speciesStats.needsAttention.length === 0 ? (
                    <p className="text-sm text-slate-500">All species are in good status.</p>
                  ) : (
                    <div className="space-y-3">
                      {speciesStats.needsAttention.map((species) => (
                        <div key={species.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {species.avatarPhotoId ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={getAvatarUrl(species.avatarPhotoId) || '/placeholder-species.jpg'}
                                alt={species.commonName}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <ExclamationTriangleIcon className="h-4 w-4 text-orange-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {species.commonName}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {!species.avatarPhotoId ? 'Missing avatar photo' : 
                               species.iucnStatus === IUCNStatus.CRITICALLY_ENDANGERED ? 'Critically endangered' :
                               species.iucnStatus === IUCNStatus.ENDANGERED ? 'Endangered' : 'Needs review'}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={speciesListPath('attention')}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    Review species needing attention
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'birds' && (
        <div className="fade-up">
          {/* Birds Analytics Charts */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Gender Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Gender Distribution
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    {Object.entries(birdStats.genderDistribution).map(([gender, count]) => (
                      <div key={gender} className="flex items-center">
                        <div className="flex items-center flex-1">
                          <div className={`h-3 w-3 rounded-full ${getGenderColor(gender as Gender)} mr-3`} />
                          <span className="text-sm text-slate-600 flex-1">{gender}</span>
                          <span className="text-sm font-medium text-slate-900">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Status Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <HeartIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Status Distribution
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    {Object.entries(birdStats.statusDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center">
                        <div className="flex items-center flex-1">
                          <div className={`h-3 w-3 rounded-full ${getBirdStatusColor(status as BirdStatus)} mr-3`} />
                          <span className="text-sm text-slate-600 flex-1">{status.replace(/_/g, ' ')}</span>
                          <span className="text-sm font-medium text-slate-900">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Species Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Birds by Species
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(birdStats.speciesDistribution)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([species, count]) => (
                      <div key={species} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate">{species}</span>
                        <span className="text-sm font-medium text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Cage Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <HomeIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Birds by Cage
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(birdStats.cageDistribution)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([cage, count]) => (
                      <div key={cage} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate">Cage {cage}</span>
                        <span className="text-sm font-medium text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Birds Recent Activity & Attention Needed */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Recently Hatched Birds */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Recently Hatched</h3>
                <div className="mt-5">
                  {birdStats.recentlyHatched.length === 0 ? (
                    <p className="text-sm text-slate-500">No recent hatchlings.</p>
                  ) : (
                    <div className="space-y-3">
                      {birdStats.recentlyHatched.map((bird) => (
                        <div key={bird.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {bird.avatarPhotoId ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={getAvatarUrl(bird.avatarPhotoId) || '/placeholder-bird.jpg'}
                                alt={bird.bandId}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <CakeIcon className="h-4 w-4 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {bird.bandId} - {bird.species?.commonName}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              Hatched {new Date(bird.hatchingDate).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              bird.gender === Gender.MALE ? 'bg-cyan-100 text-cyan-800' :
                              bird.gender === Gender.FEMALE ? 'bg-pink-100 text-pink-800' :
                              'bg-violet-100 text-violet-800'
                            }`}>
                              {bird.gender}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={birdsListPath({ filter: 'recentlyHatched' })}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    View recently hatched
                  </Link>
                </div>
              </div>
            </div>

            {/* Birds Needing Attention */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Birds Needing Attention</h3>
                <div className="mt-5">
                  {birdStats.needsAttention.length === 0 ? (
                    <p className="text-sm text-slate-500">All birds are in good status.</p>
                  ) : (
                    <div className="space-y-3">
                      {birdStats.needsAttention.map((bird) => (
                        <div key={bird.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {bird.avatarPhotoId ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={getAvatarUrl(bird.avatarPhotoId) || '/placeholder-bird.jpg'}
                                alt={bird.bandId}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <ExclamationTriangleIcon className="h-4 w-4 text-orange-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {bird.bandId} - {bird.species?.commonName}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {!bird.avatarPhotoId ? 'Missing avatar photo' : 
                               bird.status === BirdStatus.SICK ? 'Bird is sick' :
                               bird.status === BirdStatus.DECEASED ? 'Bird is deceased' : 'Needs review'}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={birdsListPath({ filter: 'attention' })}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    Review birds needing attention
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'breeding' && (
        <div className="fade-up">
          {/* Breeding Analytics Charts */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Breeding Pair Status */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <HeartIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Breeding Pair Status
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="flex items-center flex-1">
                        <div className="h-3 w-3 rounded-full bg-green-500 mr-3" />
                        <span className="text-sm text-slate-600 flex-1">Active Pairs</span>
                        <span className="text-sm font-medium text-slate-900">{breedingStats.activeBreedingPairs}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex items-center flex-1">
                        <div className="h-3 w-3 rounded-full bg-slate-300 mr-3" />
                        <span className="text-sm text-slate-600 flex-1">Retired Pairs</span>
                        <span className="text-sm font-medium text-slate-900">{breedingStats.retiredBreedingPairs}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Egg Status Distribution */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CakeIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Egg Status Distribution
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-3">
                    {Object.entries(breedingStats.eggStatusDistribution).map(([status, count]) => (
                      <div key={status} className="flex items-center">
                        <div className="flex items-center flex-1">
                          <div className={`h-3 w-3 rounded-full mr-3 ${getEggStatusColor(status as EggStatus)}`} />
                          <span className="text-sm text-slate-600 flex-1 capitalize">{formatEggStatusLabel(status)}</span>
                          <span className="text-sm font-medium text-slate-900">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Breeding by Species */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Breeding Pairs by Species
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(breedingStats.pairsBySpecies)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([species, count]) => (
                      <div key={species} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 truncate">{species}</span>
                        <span className="text-sm font-medium text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Clutch Activity */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CalendarIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-slate-500 truncate">
                        Clutches by Month
                      </dt>
                    </dl>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="space-y-2">
                    {Object.entries(breedingStats.clutchesByMonth).map(([month, count]) => (
                      <div key={month} className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">{month}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-cyan-600 h-2 rounded-full" 
                              style={{ 
                                width: `${barWidthPct(count, Object.values(breedingStats.clutchesByMonth))}%` 
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Performance Metrics */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-4">
            <div className="bg-gradient-to-r from-green-400 to-green-600 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white truncate">
                        Success Rate
                      </dt>
                      <dd className="text-3xl font-bold text-white">
                        {breedingStats.hatchingRate}%
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-400 to-blue-600 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white truncate">
                        Avg Eggs/Clutch
                      </dt>
                      <dd className="text-3xl font-bold text-white">
                        {breedingStats.avgEggsPerClutch}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-400 to-purple-600 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CakeIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white truncate">
                        Total Hatched
                      </dt>
                      <dd className="text-3xl font-bold text-white">
                        {breedingStats.totalHatched}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ArrowTrendingUpIcon className="h-8 w-8 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-white truncate">
                        This Year
                      </dt>
                      <dd className="text-3xl font-bold text-white">
                        {breedingStats.hatchedThisYear}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Breeding Activity & Analysis */}
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Most Productive Pairs */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Most Productive Pairs</h3>
                <div className="mt-5">
                  {breedingStats.productivePairs.length === 0 ? (
                    <p className="text-sm text-slate-500">No breeding data available yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {breedingStats.productivePairs.map(({ pair, totalHatched }, index) => (
                        <div key={pair.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center">
                              <span className="text-white text-sm font-bold">#{index + 1}</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {pair.maleBirdEntity?.bandId} × {pair.femaleBirdEntity?.bandId}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {(() => {
                                const maleSpecies = pair.maleBirdEntity?.species?.commonName;
                                const femaleSpecies = pair.femaleBirdEntity?.species?.commonName;
                                const species = maleSpecies || femaleSpecies || 'Unspecified Species';
                                return `${species} - Cage ${pair.cageNumber}`;
                              })()}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className="text-sm font-medium text-green-600">{totalHatched} hatched</p>
                            <p className="text-xs text-slate-500">{pair.clutches?.length || 0} clutches</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={breedingListPath({ filter: 'productive' })}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    View productive pairs
                  </Link>
                </div>
              </div>
            </div>

            {/* Pairs Needing Attention */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Pairs Needing Attention</h3>
                <div className="mt-5">
                  {breedingStats.needsAttention.length === 0 ? (
                    <p className="text-sm text-slate-500">All breeding pairs are performing well.</p>
                  ) : (
                    <div className="space-y-3">
                      {breedingStats.needsAttention.map(({ pair, reason }) => (
                        <div key={pair.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                              <ExclamationTriangleIcon className="h-4 w-4 text-orange-400" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {pair.maleBirdEntity?.bandId} × {pair.femaleBirdEntity?.bandId}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {reason}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={breedingListPath({ filter: 'attention' })}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    Review pairs needing attention
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Clutches */}
          <div className="mt-8">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900">Recent Clutches</h3>
                <div className="mt-5">
                  {breedingStats.recentClutches.length === 0 ? (
                    <p className="text-sm text-slate-500">No recent clutches.</p>
                  ) : (
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-slate-50/80">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Breeding Pair
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Eggs
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {breedingStats.recentClutches.map((clutch) => {
                            const breedingPair =
                              allBreedingPairs.find(
                                p =>
                                  p.id === clutch.breedingPairId ||
                                  p.clutches?.some(c => c.id === clutch.id),
                              ) ||
                              breedingStats.productivePairs.find(p =>
                                p.pair.clutches?.some(c => c.id === clutch.id),
                              )?.pair ||
                              breedingStats.needsAttention.find(p =>
                                p.pair.clutches?.some(c => c.id === clutch.id),
                              )?.pair;
                            
                            const eggCounts = clutch.eggs?.reduce((acc, egg) => {
                              acc[egg.status] = (acc[egg.status] || 0) + 1;
                              return acc;
                            }, {} as Record<EggStatus, number>) || {};

                            return (
                              <tr key={clutch.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {new Date(clutch.date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {breedingPair
                                    ? `${breedingPair.maleBirdEntity?.bandId || breedingPair.maleBirdEntity?.species?.commonName || 'Male'} × ${breedingPair.femaleBirdEntity?.bandId || breedingPair.femaleBirdEntity?.species?.commonName || 'Female'}`
                                    : 'Unspecified Pair'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                                  {clutch.eggs?.length || 0} eggs
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex space-x-1">
                                    {Object.entries(eggCounts).map(([status, count]) => (
                                      <span
                                        key={status}
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getEggStatusColorClass(status as EggStatus)}`}
                                      >
                                        {count as number} {formatEggStatusLabel(status)}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <Link
                    to={clutchesListPath({ filter: 'recent' })}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
                  >
                    View recent clutches
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Egg Analytics */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Egg Analytics</h2>
            
            {/* Egg Lifecycle Metrics */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 mb-8">
              <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Avg Incubation Days
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.eggLifecycleMetrics.averageIncubationDays}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-500 to-emerald-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <SparklesIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Fertility Rate
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.eggLifecycleMetrics.fertilityRate}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-orange-500 to-orange-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FireIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Hatchability Rate
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.eggLifecycleMetrics.hatchabilityRate}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-500 to-teal-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <AcademicCapIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Avg Days to Hatch
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.eggLifecycleMetrics.averageDaysToHatch}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Egg Analytics Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
              {/* Egg Failure Analysis */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartPieIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Egg Failure Analysis
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-3">
                      {Object.entries(breedingStats.eggFailureAnalysis).map(([status, data]) => (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`h-3 w-3 rounded-full ${getEggStatusColor(status as EggStatus)} mr-3`} />
                            <span className="text-sm text-slate-600 capitalize">{formatEggStatusLabel(status)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-slate-900">{data.count}</span>
                            <span className="text-xs text-slate-500">({data.percentage}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Egg Productivity by Species */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TrophyIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Egg Productivity by Species
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-3">
                      {Object.entries(breedingStats.eggProductivityBySpecies)
                        .sort(([,a], [,b]) => b.rate - a.rate)
                        .slice(0, 5)
                        .map(([species, data]) => (
                        <div key={species} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-600 truncate">{species}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-slate-900">{data.hatched}/{data.total}</span>
                              <span className={`text-sm font-semibold ${
                                data.rate >= 70 ? 'text-green-600' : 
                                data.rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {data.rate}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                data.rate >= 70 ? 'bg-green-600' : 
                                data.rate >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${data.rate}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Egg Production */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CalendarIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Egg Production by Month
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-2">
                      {Object.entries(breedingStats.eggsByMonth).map(([month, count]) => (
                        <div key={month} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{month}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-cyan-600 h-2 rounded-full" 
                                style={{ 
                                  width: `${barWidthPct(count, Object.values(breedingStats.eggsByMonth))}%` 
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Seasonal Patterns */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BoltIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Seasonal Breeding Patterns
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-slate-900">Peak Breeding Months</h4>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {breedingStats.seasonalPatterns.peakBreedingMonths.map((month) => (
                          <span key={month} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                            {month}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-900">Average Eggs by Season</h4>
                      <div className="mt-2 space-y-2">
                        {Object.entries(breedingStats.seasonalPatterns.avgEggsPerSeason).map(([season, avg]) => (
                          <div key={season} className="flex items-center justify-between">
                            <span className="text-sm text-slate-600">{season}</span>
                            <span className="text-sm font-medium text-slate-900">{avg} eggs</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Clutch Analytics */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-6">Clutch Analytics</h2>
            
            {/* Clutch Performance Metrics */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 mb-8">
              <div className="bg-gradient-to-r from-rose-500 to-rose-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClipboardDocumentListIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Clutch Frequency
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.avgClutchFrequency}
                        </dd>
                        <dt className="text-xs text-rose-200">
                          per pair/year
                        </dt>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-violet-500 to-violet-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Completion Rate
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.clutchCompletionRate}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-amber-500 to-amber-700 overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-8 w-8 text-white" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-white truncate">
                          Avg Interval
                        </dt>
                        <dd className="text-3xl font-bold text-white">
                          {breedingStats.performanceMetrics.averageClutchInterval}
                        </dd>
                        <dt className="text-xs text-amber-200">
                          days between clutches
                        </dt>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Clutch Analytics Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
              {/* Clutch Size Distribution */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Clutch Size Distribution
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-3">
                      {Object.entries(breedingStats.clutchSizeDistribution)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([size, count]) => (
                        <div key={size} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{size} eggs</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-cyan-600 h-2 rounded-full" 
                                style={{ 
                                  width: `${barWidthPct(count as number, Object.values(breedingStats.clutchSizeDistribution))}%` 
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Clutch Success Rates by Month */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TrophyIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Monthly Success Rates
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-3">
                      {Object.entries(breedingStats.clutchSuccessRates).map(([month, rate]) => (
                        <div key={month} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{month}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  rate >= 70 ? 'bg-green-600' : 
                                  rate >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                                }`}
                                style={{ width: `${Math.max(rate, 5)}%` }}
                              />
                            </div>
                            <span className={`text-sm font-medium w-12 text-right ${
                              rate >= 70 ? 'text-green-600' : 
                              rate >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {rate}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Performing Clutch Sizes */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <SparklesIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Top Performing Clutch Sizes
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="space-y-3">
                      {breedingStats.performanceMetrics.topPerformingClutchSizes.map((clutchData, index) => (
                        <div key={clutchData.size} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                              index === 0 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                              index === 1 ? 'bg-gradient-to-r from-gray-400 to-gray-600' :
                              index === 2 ? 'bg-gradient-to-r from-amber-600 to-amber-800' :
                              'bg-gradient-to-r from-blue-400 to-blue-600'
                            }`}>
                              <span className="text-white text-sm font-bold">#{index + 1}</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900">
                              {clutchData.size} eggs
                            </p>
                            <p className="text-sm text-slate-500">
                              {clutchData.successRate}% success rate
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              clutchData.successRate >= 70 ? 'bg-emerald-100 text-emerald-800' :
                              clutchData.successRate >= 50 ? 'bg-amber-100 text-amber-800' :
                              'bg-rose-100 text-rose-800'
                            }`}>
                              {clutchData.successRate}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Peak Performance Insights */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <FireIcon className="h-8 w-8 text-slate-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">
                          Peak Performance Insights
                        </dt>
                      </dl>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4">
                    <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg p-4">
                      <div className="flex items-center">
                        <TrophyIcon className="h-5 w-5 text-cyan-600 mr-2" />
                        <span className="text-sm font-medium text-indigo-900">
                          Most Productive Month
                        </span>
                      </div>
                      <p className="text-lg font-semibold text-cyan-700 mt-1">
                        {breedingStats.performanceMetrics.mostProductiveTimeOfYear || 'N/A'}
                      </p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
                      <div className="flex items-center">
                        <SparklesIcon className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-900">
                          Optimal Clutch Size
                        </span>
                      </div>
                      <p className="text-lg font-semibold text-green-700 mt-1">
                        {breedingStats.performanceMetrics.topPerformingClutchSizes[0]?.size || 'N/A'} eggs
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
                      <div className="flex items-center">
                        <ClockIcon className="h-5 w-5 text-purple-600 mr-2" />
                        <span className="text-sm font-medium text-purple-900">
                          Breeding Cycle
                        </span>
                      </div>
                      <p className="text-lg font-semibold text-purple-700 mt-1">
                        {breedingStats.performanceMetrics.averageClutchInterval} days
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 